package fr.frux.core.database.coredatabase

import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import fr.frux.core.database.ClusterPyramidCacheQueries
import fr.frux.core.database.FavoriteQueries
import fr.frux.core.database.FruxDatabase
import fr.frux.core.database.SiteQueries
import fr.frux.core.database.SpeciesCacheQueries
import fr.frux.core.database.TreeQueries
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass

internal val KClass<FruxDatabase>.schema: SqlSchema<QueryResult.Value<Unit>>
  get() = FruxDatabaseImpl.Schema

internal fun KClass<FruxDatabase>.newInstance(driver: SqlDriver): FruxDatabase = FruxDatabaseImpl(driver)

private class FruxDatabaseImpl(
  driver: SqlDriver,
) : TransacterImpl(driver),
    FruxDatabase {
  override val clusterPyramidCacheQueries: ClusterPyramidCacheQueries =
      ClusterPyramidCacheQueries(driver)

  override val favoriteQueries: FavoriteQueries = FavoriteQueries(driver)

  override val siteQueries: SiteQueries = SiteQueries(driver)

  override val speciesCacheQueries: SpeciesCacheQueries = SpeciesCacheQueries(driver)

  override val treeQueries: TreeQueries = TreeQueries(driver)

  public object Schema : SqlSchema<QueryResult.Value<Unit>> {
    override val version: Long
      get() = 8

    override fun create(driver: SqlDriver): QueryResult.Value<Unit> {
      driver.execute(null, """
          |CREATE TABLE cluster_pyramid_cache (
          |  id INTEGER PRIMARY KEY NOT NULL CHECK (id = 1),
          |  etag TEXT,
          |  payload TEXT NOT NULL,
          |  checked_at INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE favorite (
          |  tree_id INTEGER PRIMARY KEY NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE site (
          |  id INTEGER PRIMARY KEY NOT NULL,
          |  name TEXT NOT NULL,
          |  description TEXT,
          |  latitude REAL NOT NULL,
          |  longitude REAL NOT NULL,
          |  access_rule TEXT NOT NULL,
          |  manager_name TEXT,
          |  has_owner INTEGER NOT NULL DEFAULT 0,
          |  trees_count INTEGER NOT NULL DEFAULT 0,
          |  created_by INTEGER NOT NULL,
          |  created_by_username TEXT,
          |  created_at INTEGER,
          |  cached_at INTEGER NOT NULL,
          |  -- Prochaine recolte (#617) : en dernier, comme dans 6.sqm. Le statut n'est pas garde, hors ligne
          |  -- une prochaine recolte est reputee PLANNED.
          |  next_harvest_id INTEGER,
          |  next_harvest_at INTEGER,
          |  next_harvest_hosted INTEGER
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE site_planting (
          |  id INTEGER PRIMARY KEY NOT NULL,
          |  site_id INTEGER NOT NULL,
          |  species_id INTEGER NOT NULL,
          |  species_common_name_fr TEXT NOT NULL,
          |  species_scientific_name TEXT NOT NULL,
          |  species_form TEXT NOT NULL,
          |  count_estimate TEXT NOT NULL,
          |  note TEXT
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE species_cache (
          |  id INTEGER PRIMARY KEY NOT NULL CHECK (id = 1),
          |  etag TEXT,
          |  payload TEXT NOT NULL,
          |  cached_at INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE tree (
          |  id INTEGER PRIMARY KEY NOT NULL,
          |  species_id INTEGER NOT NULL,
          |  species_common_name_fr TEXT NOT NULL,
          |  species_scientific_name TEXT NOT NULL,
          |  species_form TEXT NOT NULL,
          |  harvest_part TEXT,
          |  latitude REAL NOT NULL,
          |  longitude REAL NOT NULL,
          |  status TEXT NOT NULL,
          |  certainty TEXT NOT NULL,
          |  disputed INTEGER NOT NULL DEFAULT 0,
          |  description TEXT,
          |  photo_url TEXT,
          |  -- Passage humain date (ADR 0025). Nullable : aucun humain n'a jamais rien observe. Ajoutee en
          |  -- 3.sqm par ALTER TABLE, puis remise a cette place par 4.sqm, qui recree la table pour sortir
          |  -- `verified_count` avec le vote d'identification.
          |  last_observed_at INTEGER,
          |  created_by INTEGER NOT NULL,
          |  created_at INTEGER NOT NULL,
          |  updated_at INTEGER NOT NULL,
          |  cached_at INTEGER NOT NULL,
          |  -- Lieu de rattachement (ADR 0035). Derniere colonne : ajoutee en 5.sqm par ALTER TABLE, qui
          |  -- ajoute en fin de table.
          |  site_id INTEGER
          |)
          """.trimMargin(), 0)
      driver.execute(null, "CREATE INDEX site_lat_lng ON site(latitude, longitude)", 0)
      driver.execute(null, "CREATE INDEX site_planting_site ON site_planting(site_id)", 0)
      driver.execute(null, """
          |CREATE TRIGGER site_planting_cleanup AFTER DELETE ON site
          |BEGIN
          |  DELETE FROM site_planting WHERE site_id = old.id;
          |END
          """.trimMargin(), 0)
      driver.execute(null, "CREATE INDEX tree_lat_lng ON tree(latitude, longitude)", 0)
      return QueryResult.Unit
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.Value<Unit> {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null, """
            |CREATE TABLE favorite (
            |  tree_id INTEGER PRIMARY KEY NOT NULL
            |)
            """.trimMargin(), 0)
      }
      if (oldVersion <= 2 && newVersion > 2) {
        driver.execute(null, """
            |CREATE TABLE species_cache (
            |  id INTEGER PRIMARY KEY NOT NULL CHECK (id = 1),
            |  etag TEXT,
            |  payload TEXT NOT NULL,
            |  cached_at INTEGER NOT NULL
            |)
            """.trimMargin(), 0)
      }
      if (oldVersion <= 3 && newVersion > 3) {
        driver.execute(null, "ALTER TABLE tree ADD COLUMN last_observed_at INTEGER", 0)
      }
      if (oldVersion <= 4 && newVersion > 4) {
        driver.execute(null, "DROP TABLE IF EXISTS tree", 0)
        driver.execute(null, """
            |CREATE TABLE tree (
            |  id INTEGER PRIMARY KEY NOT NULL,
            |  species_id INTEGER NOT NULL,
            |  species_common_name_fr TEXT NOT NULL,
            |  species_scientific_name TEXT NOT NULL,
            |  species_form TEXT NOT NULL,
            |  harvest_part TEXT,
            |  latitude REAL NOT NULL,
            |  longitude REAL NOT NULL,
            |  status TEXT NOT NULL,
            |  certainty TEXT NOT NULL,
            |  disputed INTEGER NOT NULL DEFAULT 0,
            |  description TEXT,
            |  photo_url TEXT,
            |  last_observed_at INTEGER,
            |  created_by INTEGER NOT NULL,
            |  created_at INTEGER NOT NULL,
            |  updated_at INTEGER NOT NULL,
            |  cached_at INTEGER NOT NULL
            |)
            """.trimMargin(), 0)
        driver.execute(null, "CREATE INDEX tree_lat_lng ON tree(latitude, longitude)", 0)
      }
      if (oldVersion <= 5 && newVersion > 5) {
        driver.execute(null, "ALTER TABLE tree ADD COLUMN site_id INTEGER", 0)
        driver.execute(null, """
            |CREATE TABLE site (
            |  id INTEGER PRIMARY KEY NOT NULL,
            |  name TEXT NOT NULL,
            |  description TEXT,
            |  latitude REAL NOT NULL,
            |  longitude REAL NOT NULL,
            |  access_rule TEXT NOT NULL,
            |  manager_name TEXT,
            |  has_owner INTEGER NOT NULL DEFAULT 0,
            |  trees_count INTEGER NOT NULL DEFAULT 0,
            |  created_by INTEGER NOT NULL,
            |  created_by_username TEXT,
            |  created_at INTEGER,
            |  cached_at INTEGER NOT NULL
            |)
            """.trimMargin(), 0)
        driver.execute(null, "CREATE INDEX site_lat_lng ON site(latitude, longitude)", 0)
        driver.execute(null, """
            |CREATE TABLE site_planting (
            |  id INTEGER PRIMARY KEY NOT NULL,
            |  site_id INTEGER NOT NULL,
            |  species_id INTEGER NOT NULL,
            |  species_common_name_fr TEXT NOT NULL,
            |  species_scientific_name TEXT NOT NULL,
            |  species_form TEXT NOT NULL,
            |  count_estimate TEXT NOT NULL,
            |  note TEXT
            |)
            """.trimMargin(), 0)
        driver.execute(null, "CREATE INDEX site_planting_site ON site_planting(site_id)", 0)
        driver.execute(null, """
            |CREATE TRIGGER site_planting_cleanup AFTER DELETE ON site
            |BEGIN
            |  DELETE FROM site_planting WHERE site_id = old.id;
            |END
            """.trimMargin(), 0)
      }
      if (oldVersion <= 6 && newVersion > 6) {
        driver.execute(null, "ALTER TABLE site ADD COLUMN next_harvest_id INTEGER", 0)
        driver.execute(null, "ALTER TABLE site ADD COLUMN next_harvest_at INTEGER", 0)
        driver.execute(null, "ALTER TABLE site ADD COLUMN next_harvest_hosted INTEGER", 0)
      }
      if (oldVersion <= 7 && newVersion > 7) {
        driver.execute(null, """
            |CREATE TABLE cluster_pyramid_cache (
            |  id INTEGER PRIMARY KEY NOT NULL CHECK (id = 1),
            |  etag TEXT,
            |  payload TEXT NOT NULL,
            |  checked_at INTEGER NOT NULL
            |)
            """.trimMargin(), 0)
      }
      return QueryResult.Unit
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.Value<Unit> {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion + 1)
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion)
      }
      return QueryResult.Unit
    }
  }
}
